/**
 * Loads `GET /credit-limits/requests` for the Requests tab (B4) and keeps the list current after an action: the
 * action's answer replaces its request, so the buttons follow the new `allowedActions` without a reload. A failed
 * load is reported, never shown as an empty list (CLAUDE.md, learning «an empty dropdown can be a 500»).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { creditLimitsApi } from '../../../services/creditLimitsApi';
import type { CreditLimitRequestDto, NavAction, RequestAction } from '../../../types/creditLimits';
import { apiErrorText, replaceRequest } from './requestRules';

/** What the dialog sends: an approval action (body of the actions call) or a NAV action (C2). */
export interface RequestActionInput {
    action: RequestAction | NavAction;
    comment: string | null;
    onBehalf: boolean;
}

export interface UseCreditLimitRequests {
    requests: CreditLimitRequestDto[] | null;
    loading: boolean;
    error: string | null;
    reload: () => void;
    /** Sends an action; resolves with the new request, rejects with the API's error (status on the error). */
    act: (id: number, body: RequestActionInput) => Promise<CreditLimitRequestDto>;
}

export function useCreditLimitRequests(): UseCreditLimitRequests {
    const [requests, setRequests] = useState<CreditLimitRequestDto[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [trigger, setTrigger] = useState(0);
    const counter = useRef(0);

    useEffect(() => {
        const mine = ++counter.current;
        setLoading(true);
        setError(null);
        creditLimitsApi.getRequests()
            .then(list => { if (mine === counter.current) setRequests(list.requests); })
            .catch(err => { if (mine === counter.current) setError(apiErrorText(err, 'Failed to load the requests')); })
            .finally(() => { if (mine === counter.current) setLoading(false); });
    }, [trigger]);

    const reload = useCallback(() => setTrigger(n => n + 1), []);

    const act = useCallback(async (id: number, body: RequestActionInput) => {
        let next: CreditLimitRequestDto;
        try {
            if (body.action === 'nav_write') next = await creditLimitsApi.navWrite(id);
            else if (body.action === 'nav_check') next = await creditLimitsApi.navCheck(id);
            else if (body.action === 'nav_manual') next = await creditLimitsApi.navManual(id, body.comment);
            else next = await creditLimitsApi.requestAction(id, { action: body.action, comment: body.comment, onBehalf: body.onBehalf });
        } catch (err) {
            // A NAV call can fail after the request moved on (writing, error text): show the real state next to the message.
            if (body.action === 'nav_write' || body.action === 'nav_check' || body.action === 'nav_manual') setTrigger(n => n + 1);
            throw err;
        }
        setRequests(list => (list ? replaceRequest(list, next) : [next]));
        return next;
    }, []);

    return { requests, loading, error, reload, act };
}
